import { makeStyles } from '@fluentui/react-components';
import { tokens } from '@bingads-webui-fluent-component/tokens';

/**
 * Fluent V2 overrides for Bootstrap/legacy form fields.
 * Shared by Account Details and Payment steps.
 * Covers .form-control, .form-group, .btn, .btn-primary, labels,
 * and Fluent V1 TextField (.ms-TextField).
 */
export const useV2FormFieldOverrides = makeStyles({
  formFieldOverrides: {
    // Form group spacing
    '& .form-group': {
      marginBottom: tokens.spacingVerticalM,
      marginTop: '0',
    },
    '& .form-group:last-child': {
      marginBottom: '0',
    },
    // Labels: Body 1 Strong per Figma (14px semibold)
    '& .form-group label': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      paddingBottom: tokens.spacingVerticalXS,
    },
    '& label.control-label': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      paddingBottom: tokens.spacingVerticalXS,
    },
    // Input fields
    '& .form-control': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRadius: tokens.borderRadiusMedium,
      height: tokens.spacingVerticalXXXL,
      paddingLeft: tokens.spacingHorizontalMNudge,
      paddingRight: tokens.spacingHorizontalMNudge,
      paddingTop: '0',
      paddingBottom: '0',
      outlineStyle: 'none',
      boxSizing: 'border-box',
      transitionProperty: 'none',
      boxShadow: 'none',
    },
    '& input.form-control, & textarea.form-control, & select.form-control': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& input.form-control:hover, & textarea.form-control:hover, & select.form-control:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& input.form-control:focus, & textarea.form-control:focus, & select.form-control:focus': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: tokens.strokeWidthThick,
    },
    '& .form-control::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
    // Select/dropdown controls
    '& select.form-control': {
      appearance: 'none',
      // eslint-disable-next-line quotes
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2.22 4.47a.75.75 0 0 1 1.06 0L6 7.19l2.72-2.72a.75.75 0 0 1 1.06 1.06L6.53 8.78a.75.75 0 0 1-1.06 0L2.22 5.53a.75.75 0 0 1 0-1.06z' fill='%23424242'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: `right ${tokens.spacingHorizontalMNudge} center`,
      paddingRight: tokens.spacingHorizontalXXXL,
    },
    // Fluent V1 TextField overrides
    '& .ms-TextField .ms-TextField-fieldGroup': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      backgroundColor: tokens.colorNeutralBackground1,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRadius: tokens.borderRadiusMedium,
      height: tokens.spacingVerticalXXXL,
      boxSizing: 'border-box',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .ms-TextField:hover .ms-TextField-fieldGroup': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .ms-TextField.is-active .ms-TextField-fieldGroup': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: tokens.strokeWidthThick,
    },
    '& .ms-TextField-fieldGroup::after': {
      borderTopWidth: '0 !important',
      borderRightWidth: '0 !important',
      borderBottomWidth: '0 !important',
      borderLeftWidth: '0 !important',
    },
    '& .ms-TextField .ms-TextField-field': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      paddingLeft: tokens.spacingHorizontalMNudge,
      paddingRight: tokens.spacingHorizontalMNudge,
      outlineStyle: 'none',
    },
    '& .ms-TextField .ms-TextField-field::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
    '& .ms-TextField .ms-Label': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      paddingBottom: tokens.spacingVerticalXS,
    },
    // Fluent V1 DefaultButton overrides
    '& .ms-Button.ms-Button--default': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: tokens.colorNeutralStroke1,
      borderRightColor: tokens.colorNeutralStroke1,
      borderBottomColor: tokens.colorNeutralStroke1,
      borderLeftColor: tokens.colorNeutralStroke1,
      backgroundColor: tokens.colorNeutralBackground1,
      color: tokens.colorNeutralForeground1,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      cursor: 'pointer',
      outlineStyle: 'none',
      minWidth: 'auto',
      height: tokens.spacingVerticalXXXL,
    },
    '& .ms-Button.ms-Button--default:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      color: tokens.colorNeutralForeground1Hover,
    },
    '& .ms-Button.ms-Button--default:active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      borderTopColor: tokens.colorNeutralStroke1Pressed,
      borderRightColor: tokens.colorNeutralStroke1Pressed,
      borderBottomColor: tokens.colorNeutralStroke1Pressed,
      borderLeftColor: tokens.colorNeutralStroke1Pressed,
      color: tokens.colorNeutralForeground1Pressed,
    },
    '& .ms-Button.ms-Button--default:disabled, & .ms-Button.ms-Button--default.is-disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: tokens.colorNeutralStrokeDisabled,
      borderRightColor: tokens.colorNeutralStrokeDisabled,
      borderBottomColor: tokens.colorNeutralStrokeDisabled,
      borderLeftColor: tokens.colorNeutralStrokeDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
    // Fluent V1 PrimaryButton overrides
    '& .ms-Button.ms-Button--primary': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      backgroundColor: tokens.colorBrandBackground,
      borderTopColor: tokens.colorBrandBackground,
      borderRightColor: tokens.colorBrandBackground,
      borderBottomColor: tokens.colorBrandBackground,
      borderLeftColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      cursor: 'pointer',
      outlineStyle: 'none',
      minWidth: 'auto',
      height: tokens.spacingVerticalXXXL,
    },
    '& .ms-Button.ms-Button--primary:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      borderTopColor: tokens.colorBrandBackgroundHover,
      borderRightColor: tokens.colorBrandBackgroundHover,
      borderBottomColor: tokens.colorBrandBackgroundHover,
      borderLeftColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& .ms-Button.ms-Button--primary:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      borderTopColor: tokens.colorBrandBackgroundPressed,
      borderRightColor: tokens.colorBrandBackgroundPressed,
      borderBottomColor: tokens.colorBrandBackgroundPressed,
      borderLeftColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& .ms-Button.ms-Button--primary:disabled, & .ms-Button.ms-Button--primary.is-disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: tokens.colorNeutralStrokeDisabled,
      borderRightColor: tokens.colorNeutralStrokeDisabled,
      borderBottomColor: tokens.colorNeutralStrokeDisabled,
      borderLeftColor: tokens.colorNeutralStrokeDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
    // Full width form controls
    '& .col-xs-6': {
      width: '100%',
    },
    // Error text styling
    '& .error': {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorPaletteRedForeground1,
      lineHeight: tokens.lineHeightBase200,
    },
    // Bootstrap .btn
    '& .btn': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: tokens.colorNeutralStroke1,
      borderRightColor: tokens.colorNeutralStroke1,
      borderBottomColor: tokens.colorNeutralStroke1,
      borderLeftColor: tokens.colorNeutralStroke1,
      backgroundColor: tokens.colorNeutralBackground1,
      color: tokens.colorNeutralForeground1,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      cursor: 'pointer',
      outlineStyle: 'none',
    },
    '& .btn:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      color: tokens.colorNeutralForeground1Hover,
    },
    '& .btn:active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      borderTopColor: tokens.colorNeutralStroke1Pressed,
      borderRightColor: tokens.colorNeutralStroke1Pressed,
      borderBottomColor: tokens.colorNeutralStroke1Pressed,
      borderLeftColor: tokens.colorNeutralStroke1Pressed,
      color: tokens.colorNeutralForeground1Pressed,
    },
    '& .btn-primary': {
      backgroundColor: tokens.colorBrandBackground,
      borderTopColor: tokens.colorBrandBackground,
      borderRightColor: tokens.colorBrandBackground,
      borderBottomColor: tokens.colorBrandBackground,
      borderLeftColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& .btn-primary:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      borderTopColor: tokens.colorBrandBackgroundHover,
      borderRightColor: tokens.colorBrandBackgroundHover,
      borderBottomColor: tokens.colorBrandBackgroundHover,
      borderLeftColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& .btn-primary:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      borderTopColor: tokens.colorBrandBackgroundPressed,
      borderRightColor: tokens.colorBrandBackgroundPressed,
      borderBottomColor: tokens.colorBrandBackgroundPressed,
      borderLeftColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
});
